import { SearchType } from '@aibees/elephant/types';
import { ErrorResponse, HttpMethod } from '@aibees/shared/definition';
import { useApiComparison, useEnvironment } from '@aibees/shared/hooks';
import { useBaseQuery } from '@aibees/shared/http';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import type { UseQueryOptions } from '@tanstack/react-query';
import { omit } from 'lodash-es';
import { useDeferredValue } from 'react';
import {
  COMPANY_SEARCH_API_METADATA_PREFIX,
  PEOPLE_SEARCH_API_METADATA_PREFIX,
} from '../constants';
import { CompanySearchBody } from '../feature/search-table-company/hooks/types';
import { transform as companyRequestTransform } from '../feature/search-table-company/hooks/use-browse-company-search';
import { transform as peopleRequestTransform } from '../feature/search-table-people/hooks/use-browse-people-search';
import { BrowseSearchIdsRequest, BrowseSearchIdsResponse } from './types';
import {
  useSearchModeFromRoute,
  useSearchTypeFromRoute,
} from './use-obtain-search-type-from-route';
import { useSearchQueryFilter } from './use-search-query-filter';
import { useTrackEvents } from './use-track-events';

export function useBrowseSearchIds(
  request: BrowseSearchIdsRequest,
  queryParams?: Record<string, unknown>,
  enabled?: boolean,
  options?: Pick<
    UseQueryOptions<BrowseSearchIdsResponse, ErrorResponse>,
    'onSuccess' | 'staleTime' | 'cacheTime'
  >
) {
  const workspace = useWorkspace()?.id;
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  let url = COMPANY_SEARCH_API_METADATA_PREFIX;
  const { track } = useTrackEvents();
  const { isStaging } = useEnvironment();
  const isNewApiEnabled = !!enabled && isStaging && type === SearchType.People;

  if (type === SearchType.People) url = PEOPLE_SEARCH_API_METADATA_PREFIX;

  const reqParams = objSanitizeNilValues(
    omit({ ...request, ...deferredParams }, 'type', 'page', 'size')
  );

  // transform
  const transformedRequestParams =
    type === SearchType.People
      ? peopleRequestTransform(reqParams)
      : companyRequestTransform(reqParams, mode);

  // !!=======================================
  // !! IMPORTANT: do not remove or use this query, it is here only to test the new API
  const testNewAPI = useBaseQuery<
    CompanySearchBody &
      Omit<BrowseSearchIdsRequest, 'sort'> & { sort?: [string] },
    BrowseSearchIdsResponse
  >(
    [
      'testNewAPI',
      type,
      queryParams,
      {
        ...transformedRequestParams,
        size: request.size,
      },
    ],
    {
      url: url?.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      params: { return: 'id_pair' },
      data: {
        ...transformedRequestParams,
        ...{
          size: request.size,
          page: 0, // it should be always zero
        },
      },
    },
    {
      enabled: isNewApiEnabled,
      ...{
        ...options,
        onSuccess: () => {
          comparison.onApiBSuccess?.();
        },
      },
    }
  );
  // !!=======================================

  const result = useBaseQuery<
    CompanySearchBody &
      Omit<BrowseSearchIdsRequest, 'sort'> & { sort?: [string] },
    BrowseSearchIdsResponse
  >(
    [
      useBrowseSearchIds.name,
      type,
      queryParams,
      {
        ...transformedRequestParams,
        size: request.size,
      },
    ],
    {
      url: url?.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      params: queryParams,
      data: {
        ...transformedRequestParams,
        ...{
          size: request.size,
          page: 0, // it should be always zero
        },
      },
    },
    {
      enabled,
      ...{
        ...options,
        onSuccess: (res) => {
          comparison.onApiASuccess?.();
          options?.onSuccess?.(res);
        },
      },
    }
  );

  const comparison = useApiComparison({
    key: `browse-search-ids:${type}`,
    enabled: isNewApiEnabled,
    apiA: { label: 'old', isFetching: result.isFetching },
    apiB: { label: 'new', isFetching: testNewAPI.isFetching },
    onCompare: (data) => {
      console.info('[API Comparison]', data);
      track('API_RESPONSE_TIME_COMPARISON', data);
    },
  });

  return result;
}
